import { useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, FileBox } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { createDigitalTwin } from "../../lib/digitalTwinsApi";

const ALLOWED_EXTENSIONS = ["ply", "splat", "spz", "ksplat"];

interface UploadDigitalTwinModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualSiteId: string;
  onCreated: () => void;
}

export function UploadDigitalTwinModal({
  isOpen, onClose, virtualSiteId, onCreated,
}: UploadDigitalTwinModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFile(null);
    setName("");
    setDescription("");
    setLatitude("");
    setLongitude("");
    setProgress(0);
    setError(null);
  }

  /**
   * Validate the optional coordinate pair. Returns the parsed numbers, or
   * `null` when both are blank (georeference is optional), or throws a
   * user-facing message on a bad/partial entry so the twin can't land on the
   * map at a nonsensical point.
   */
  function parseCoords(): { latitude: number; longitude: number } | null {
    const latRaw = latitude.trim();
    const lngRaw = longitude.trim();
    if (!latRaw && !lngRaw) return null;
    if (!latRaw || !lngRaw) {
      throw new Error("Enter both latitude and longitude, or leave both blank.");
    }
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      throw new Error("Latitude must be a number between −90 and 90.");
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      throw new Error("Longitude must be a number between −180 and 180.");
    }
    return { latitude: lat, longitude: lng };
  }

  function handleClose() {
    // Closing during an upload aborts it instead of being a no-op. The
    // createDigitalTwin flow will catch the abort, DELETE the half-created
    // row, and we wipe local state.
    if (isUploading) {
      abortRef.current?.abort();
      return;
    }
    reset();
    onClose();
  }

  function pickFile(picked: File) {
    setError(null);
    const ext = picked.name.split(".").pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Unsupported file. Allowed: .${ALLOWED_EXTENSIONS.join(", .")}`);
      return;
    }
    setFile(picked);
    if (!name) setName(picked.name.replace(/\.[^.]+$/, ""));
  }

  async function handleUpload() {
    if (!file || !name) {
      setError("Pick a file and give it a name");
      return;
    }
    let coords: { latitude: number; longitude: number } | null;
    try {
      coords = parseCoords();
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    setIsUploading(true);
    setError(null);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await createDigitalTwin(
        {
          virtual_site_id: virtualSiteId,
          name,
          description: description || undefined,
          file,
          latitude: coords?.latitude,
          longitude: coords?.longitude,
        },
        (loaded, total) => setProgress(Math.round((loaded / total) * 100)),
        { signal: controller.signal },
      );
      onCreated();
      reset();
      onClose();
    } catch (err: unknown) {
      if (axios.isCancel(err) || (err as { name?: string })?.name === "CanceledError") {
        // User-initiated cancel: refresh the list (the row was cleaned up
        // server-side) and close without an error banner.
        onCreated();
        reset();
        onClose();
        return;
      }
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      const detail = e.response?.data?.detail;
      if (e.response?.status === 413) {
        setError(detail || "File exceeds the server upload limit.");
      } else if (e.response?.status === 403) {
        setError("You don't have permission to upload digital twins on this site.");
      } else {
        setError(detail || "Upload failed. Please try again.");
      }
    } finally {
      setIsUploading(false);
      abortRef.current = null;
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Digital Twin" size="lg">
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) pickFile(dropped);
          }}
          className="group cursor-pointer rounded-xl border-2 border-dashed border-surface-700 bg-surface-900/40 px-6 py-10 text-center transition-colors hover:border-brand-500/60 hover:bg-brand-500/5"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.map((e) => "." + e).join(",")}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) pickFile(f);
            }}
          />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-500/15">
                <FileBox className="h-6 w-6 text-brand-300" />
              </div>
              <p className="text-sm font-medium text-surface-100">{file.name}</p>
              <p className="text-xs text-surface-500">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-surface-800 group-hover:bg-brand-500/15">
                <UploadCloud className="h-6 w-6 text-surface-400 group-hover:text-brand-300" />
              </div>
              <p className="text-sm font-medium text-surface-200">
                Drop a 3DGS file here, or click to browse
              </p>
              <p className="text-xs text-surface-500">
                Supported: .{ALLOWED_EXTENSIONS.join(", .")}
              </p>
            </div>
          )}
        </div>

        <Input
          label="Name"
          placeholder="e.g., Warehouse roof scan"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={isUploading}
        />
        <Input
          label="Description"
          placeholder="Optional"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isUploading}
        />

        <div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Latitude"
              type="number"
              inputMode="decimal"
              step="any"
              placeholder="e.g., 51.5074"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              disabled={isUploading}
            />
            <Input
              label="Longitude"
              type="number"
              inputMode="decimal"
              step="any"
              placeholder="e.g., -0.1278"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              disabled={isUploading}
            />
          </div>
          <p className="mt-1.5 text-xs text-surface-500">
            Optional — WGS84 decimal degrees. Places this twin on the 2D map.
          </p>
        </div>

        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-surface-400">
              <span>Uploading…</span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-800">
              <div
                className="h-full bg-gradient-to-r from-brand-500 to-purple-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose}>
            {isUploading ? "Cancel upload" : "Cancel"}
          </Button>
          <Button onClick={handleUpload} isLoading={isUploading} disabled={!file || !name}>
            Upload
          </Button>
        </div>
      </div>
    </Modal>
  );
}
